import { useQuery } from "@apollo/client";
import _ from "lodash";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import Hidden from "@mui/material/Hidden";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import Toolbar from "@mui/material/Toolbar";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DateRangeIcon from "@mui/icons-material/DateRange";
import EventIcon from "@mui/icons-material/Event";
import InboxIcon from "@mui/icons-material/Inbox";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MenuIcon from "@mui/icons-material/Menu";
import MessageIcon from "@mui/icons-material/Message";
import PhoneIcon from "@mui/icons-material/Phone";

import { openCalPopup } from "~/shared/utils/calcomEmbed.js";
import { isNullDate } from "~/shared/utils/sharedUtils";
import imgLogo from "~assets/images/logo.svg";

import { clearCookies } from "../utils/portalCookies";

import {
  GET_DOCTOR_PATIENT_MESSAGES,
  GET_PATIENT_CARE_MESSAGES,
  GET_PATIENT_RECORD,
} from "./portalQueries";

import {
  drawerWidth,
  headerHeight,
  kickBlue,
  kickNavy,
} from "../styles/portalStyles";

const useHeaderStyles = makeStyles((theme) => ({
  logo: {
    height: "30px",
    // border: "1px solid red",
    margin: "30px 0 0 20px",
    alignSelf: "flex-start",
    [theme.breakpoints.down("md")]: {
      // margin: "20px 0 0 0",
    },
  },
  linksContainer: {
    display: "flex",
    flex: 1,
    justifyContent: "flex-end",
    // margin: "44px 60px 0 0",
    margin: "30px 0px 0 0",
    // backgroundColor: "white",
    [theme.breakpoints.down("md")]: {
      margin: "14px 10px 0 0",
    },
  },
}));

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  drawer: {
    [theme.breakpoints.up("sm")]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  appBar: {
    [theme.breakpoints.up("sm")]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
    // backgroundColor: "white",
    backgroundColor: kickBlue,

    // zIndex: theme.zIndex.drawer + 1,
  },
  menuButton: {
    color: "white",
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(1),
    [theme.breakpoints.up("sm")]: {
      display: "none",
    },
  },
  // necessary for content to be below app bar
  toolbar: theme.mixins.toolbar,
  drawerPaper: {
    width: drawerWidth,
  },

  appBarNavLink: {
    fontFamily: "BasisGrotesquePro-Bold",
    letterSpacing: "0.03em",
    marginRight: "30px",
    // color: "#1e2347",
    color: "white",
  },

  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
}));

function UnreadMsgsIcon() {
  return (
    <div
      style={{
        backgroundColor: "red",
        borderRadius: "50%",
        width: "10px",
        height: "10px",
        position: "absolute",
        left: "30px",
        top: "10px",
      }}
    />
  );
}

export default function PortalHeader(props) {
  let history = useHistory();
  const theme = useTheme();
  const classes = useStyles();
  const headerStyles = useHeaderStyles();
  const [mobileOpen, setMobileOpen] = useState(false);

  const { data: drMsgData } = useQuery(GET_DOCTOR_PATIENT_MESSAGES);
  const { data: careMsgData } = useQuery(GET_PATIENT_CARE_MESSAGES);
  const { data: patientData } = useQuery(GET_PATIENT_RECORD);

  const lastMsgFromDoctor = _.last(
    drMsgData?.doctorPatientMessages.filter((msg) => msg.emrUserFirstName),
  );
  const hasUnreadDrMsgs =
    lastMsgFromDoctor && isNullDate(lastMsgFromDoctor.readAt);
  const lastMsgFromCare = _.last(
    careMsgData?.patientCareMessages.filter((msg) => msg.emrUserFirstName),
  );
  const hasUnreadCareMsgs =
    lastMsgFromCare && isNullDate(lastMsgFromCare.readAt);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const shouldShowSleepConciergeCTA = (() => {
    if (!patientData?.patientRecord) return false;
    if (patientData.patientRecord.hasCompletedMeeting) return false;
    const patient = patientData.patientRecord.patient;
    if (!patient.personaVerified) return false;
    const visits = patientData.patientRecord.visits || [];

    // Already booked - don't show
    if (
      patient.sleepConciergeBookedAt &&
      !isNullDate(patient.sleepConciergeBookedAt)
    ) {
      return false;
    }

    // Find completed insomnia initial visits (visitType "IN")
    const completedInsomniaVisits = visits.filter(
      (v) => v.visitType === "IN" && !isNullDate(v.patientCompletedAt),
    );
    if (completedInsomniaVisits.length === 0) return false;

    // Check if the first completed insomnia visit is within last 30 days
    const firstCompleted = completedInsomniaVisits[0];
    const completedDate = new Date(firstCompleted.patientCompletedAt);
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    return completedDate >= cutoff;
  })();

  const drawer = (isMobile) => {
    return (
      <div>
        <List>
          <ListItem
            button
            onClick={() => {
              isMobile && handleDrawerToggle();
              history.push("/me/refills");
            }}
            key="treatments"
            style={{ color: kickNavy }}
          >
            <ListItemIcon style={{ color: kickNavy }}>
              <LocalHospitalIcon style={{ color: kickNavy }} />
            </ListItemIcon>
            <ListItemText primary="Treatments" />
          </ListItem>

          <ListItem
            button
            onClick={() => {
              isMobile && handleDrawerToggle();
              history.push("/me/my-subscriptions");
            }}
            key="mySubscriptions"
            style={{ color: kickNavy }}
          >
            <ListItemIcon style={{ color: kickNavy }}>
              <DateRangeIcon style={{ color: kickNavy }} />
            </ListItemIcon>
            <ListItemText primary="My Subscriptions" />
          </ListItem>

          <ListItem
            button
            key="Support"
            style={{ color: kickNavy }}
            onClick={() => {
              isMobile && handleDrawerToggle();
              history.push("/me/messages/care");
            }}
          >
            <ListItemIcon style={{ color: kickNavy }}>
              <MessageIcon style={{ color: kickNavy }} />
            </ListItemIcon>
            <ListItemText primary="Message Us" />
            {hasUnreadCareMsgs && <UnreadMsgsIcon />}
          </ListItem>

          <ListItem
            button
            key="messageDoctor"
            onClick={() => {
              isMobile && handleDrawerToggle();
              history.push("/me/messages/doctor");
            }}
            style={{ color: kickNavy }}
          >
            <ListItemIcon style={{ color: kickNavy }}>
              <InboxIcon style={{ color: kickNavy }} />
            </ListItemIcon>
            <ListItemText primary="Text Doctor" />
            {hasUnreadDrMsgs && <UnreadMsgsIcon />}
          </ListItem>

          <ListItem
            button
            onClick={() => {
              isMobile && handleDrawerToggle();
              history.push("/me/payment-portal");
            }}
            key="paymentsBilling"
            style={{ color: kickNavy }}
          >
            <ListItemIcon style={{ color: kickNavy }}>
              <CreditCardIcon style={{ color: kickNavy }} />
            </ListItemIcon>
            <ListItemText primary="Payments & Billing" />
          </ListItem>

          {patientData?.patientRecord?.upcomingMeeting ? (
            <ListItem
              button
              onClick={() => {
                isMobile && handleDrawerToggle();
                openCalPopup(
                  `booking/${patientData.patientRecord.upcomingMeeting.calcomBookingUID}`,
                );
              }}
              key="upcomingMeeting"
              style={{ color: kickNavy }}
            >
              <ListItemIcon style={{ color: kickNavy }}>
                <EventIcon style={{ color: kickNavy }} />
              </ListItemIcon>
              <ListItemText
                primary="Upcoming Sleep Call"
                secondary={new Date(
                  patientData.patientRecord.upcomingMeeting.startsAt,
                ).toLocaleString("en-US", {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              />
            </ListItem>
          ) : (
            shouldShowSleepConciergeCTA && (
              <ListItem
                button
                onClick={() => {
                  isMobile && handleDrawerToggle();
                  openCalPopup("sleep-concierge/introduction", {
                    userID: patientData?.patientRecord?.patient?.id || "",
                    userEmail: patientData?.patientRecord?.patient?.email || "",
                    userName:
                      `${patientData?.patientRecord?.patient?.firstName || ""} ${patientData?.patientRecord?.patient?.lastName || ""}`.trim(),
                    userPhone: patientData?.patientRecord?.patient?.phone || "",
                  });
                }}
                key="sleepConcierge"
                style={{ color: kickNavy }}
              >
                <ListItemIcon style={{ color: kickNavy }}>
                  <PhoneIcon style={{ color: kickNavy }} />
                </ListItemIcon>
                <ListItemText
                  primary="Schedule Sleep Call"
                  secondary="Free 15-min consultation"
                />
              </ListItem>
            )
          )}
        </List>
      </div>
    );
  };

  // const container =
  //   window !== undefined ? () => window().document.body : undefined;

  const renderLinks = () => {
    // only render links if logged in
    if (!props.loggedIn) return;
    if (window.location.pathname == "/me/payment-portal") return;

    return (
      <div className={headerStyles.linksContainer}>
        <Hidden smDown implementation="css">
          <Link href="/doctors" className={classes.appBarNavLink}>
            Our Doctors
          </Link>
          <Link href="/about" className={classes.appBarNavLink}>
            Story
          </Link>
          <Link href="/beta-blockers" className={classes.appBarNavLink}>
            Performance
          </Link>
          <Link href="/sleep" className={classes.appBarNavLink}>
            Sleep
          </Link>
          <Link href="/reviews" className={classes.appBarNavLink}>
            Reviews
          </Link>
          <Link href="/faq" className={classes.appBarNavLink}>
            FAQ
          </Link>
          <Link href="/guides" className={classes.appBarNavLink}>
            Guides
          </Link>
        </Hidden>
        <Link
          onClick={() => {
            clearCookies();
            window.location.replace("/");
          }}
          className={classes.appBarNavLink}
          style={{ marginLeft: "10px", marginRight: "10px" }}
        >
          Logout
        </Link>
      </div>
    );
  };

  return (
    <div className={classes.root}>
      <AppBar
        position="fixed"
        className={classes.appBar}
        style={{ height: headerHeight }}
      >
        <Toolbar>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            className={classes.menuButton}
          >
            <MenuIcon />
          </IconButton>

          {renderLinks()}
        </Toolbar>
      </AppBar>
      <nav className={classes.drawer} aria-label="side navigation">
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}

        <Hidden smUp implementation="css">
          <Drawer
            // container={container}
            variant="temporary"
            anchor={theme.direction === "rtl" ? "right" : "left"}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            classes={{
              paper: classes.drawerPaper,
            }}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
          >
            <a href="/">
              <img
                className={headerStyles.logo}
                style={{ marginBottom: "20px" }}
                src={imgLogo}
              />
            </a>

            {drawer(true)}
          </Drawer>
        </Hidden>
        <Hidden smDown implementation="css">
          <Drawer
            classes={{
              paper: classes.drawerPaper,
            }}
            variant="permanent"
            open
          >
            <a href="/">
              <img className={headerStyles.logo} src={imgLogo} />
            </a>
            <Divider style={{ marginTop: "40px" }} />
            {drawer(false)}
          </Drawer>
        </Hidden>
      </nav>
    </div>
  );
}
